import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send, Loader2, User as UserIcon, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { handleQuotaError } from "@/lib/quota";

// Lightweight markdown renderer (avoid heavy deps): supports headings, bullets, bold, inline code, code fences.
function renderMd(src) {
  if (!src) return null;
  const lines = src.split("\n");
  const out = [];
  let i = 0;
  let key = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith("```")) {
      const lang = line.slice(3).trim();
      const buf = [];
      i++;
      while (i < lines.length && !lines[i].startsWith("```")) {
        buf.push(lines[i]);
        i++;
      }
      i++;
      out.push(
        <pre
          key={key++}
          className="font-mono text-xs bg-secondary/60 rounded-sm p-3 overflow-x-auto my-2 border border-border/40"
        >
          <code>{buf.join("\n")}</code>
        </pre>
      );
      continue;
    }
    if (line.startsWith("### ")) {
      out.push(<h4 key={key++} className="font-display font-semibold text-base mt-3">{inline(line.slice(4))}</h4>);
    } else if (line.startsWith("## ")) {
      out.push(<h3 key={key++} className="font-display font-semibold text-lg mt-4">{inline(line.slice(3))}</h3>);
    } else if (line.startsWith("# ")) {
      out.push(<h2 key={key++} className="font-display font-semibold text-xl mt-4">{inline(line.slice(2))}</h2>);
    } else if (/^\s*[-*]\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s/, ""));
        i++;
      }
      out.push(
        <ul key={key++} className="list-disc list-inside space-y-1 my-2 ps-2">
          {items.map((it, idx) => <li key={idx} className="text-sm">{inline(it)}</li>)}
        </ul>
      );
      continue;
    } else if (line.trim() === "") {
      out.push(<div key={key++} className="h-2" />);
    } else {
      out.push(<p key={key++} className="text-sm leading-relaxed">{inline(line)}</p>);
    }
    i++;
  }
  return out;
}

function inline(text) {
  // **bold**, *italic*, `code`
  const parts = [];
  let rest = text;
  let key = 0;
  const re = /(\*\*([^*]+)\*\*|`([^`]+)`|\*([^*]+)\*)/;
  while (rest.length) {
    const m = rest.match(re);
    if (!m) {
      parts.push(rest);
      break;
    }
    if (m.index > 0) parts.push(rest.slice(0, m.index));
    if (m[2]) parts.push(<strong key={key++}>{m[2]}</strong>);
    else if (m[3]) parts.push(<code key={key++} className="font-mono text-[12px] px-1 rounded bg-secondary/60">{m[3]}</code>);
    else if (m[4]) parts.push(<em key={key++}>{m[4]}</em>);
    rest = rest.slice(m.index + m[0].length);
  }
  return parts;
}

export default function ChatPane({ product, endpoint, accent, placeholder, starters = [], onSent, stream = false, streamProduct = null }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const sendStreaming = (text) => {
    const token = localStorage.getItem("sanaa_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/chat/stream/${streamProduct}?message=${encodeURIComponent(text)}${sessionId ? `&session_id=${sessionId}` : ""}&token=${encodeURIComponent(token)}`;
    return new Promise((resolve, reject) => {
      const es = new EventSource(url);
      const assistantId = `a-${Date.now()}`;
      let assembled = "";
      setMessages((m) => [
        ...m,
        { id: `u-${Date.now()}`, role: "user", content: text },
        { id: assistantId, role: "assistant", content: "" },
      ]);
      es.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.type === "chunk") {
            assembled += data.content;
            setMessages((m) => m.map((mm) => mm.id === assistantId ? { ...mm, content: assembled } : mm));
          } else if (data.type === "done") {
            assembled = data.content || assembled;
            setMessages((m) => m.map((mm) => mm.id === assistantId ? { ...mm, content: assembled } : mm));
          } else if (data.type === "session") {
            setSessionId(data.session_id);
          } else if (data.type === "error") {
            toast.error(data.content || "Stream failed");
            es.close();
            reject(new Error(data.content));
          }
        } catch { /* ignore */ }
      };
      es.onerror = () => {
        es.close();
        resolve();
      };
      es.addEventListener("end", () => { es.close(); resolve(); });
      // Auto-close after 2 minutes hard timeout
      setTimeout(() => { es.close(); resolve(); }, 120000);
    });
  };

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || loading) return;
    setInput("");
    setLoading(true);
    try {
      if (stream && streamProduct) {
        await sendStreaming(t);
        onSent && onSent();
      } else {
        setMessages((m) => [
          ...m,
          { id: `u-${Date.now()}`, role: "user", content: t },
          { id: `tmp-${Date.now()}`, role: "assistant", content: "…", pending: true },
        ]);
        const r = await api.post(endpoint, { message: t, session_id: sessionId });
        setSessionId(r.data.session_id);
        setMessages(r.data.messages);
        onSent && onSent();
      }
    } catch (e) {
      if (!handleQuotaError(e, nav)) {
        toast.error(e?.response?.data?.detail || "Chat failed");
      }
      setMessages((m) => m.filter((mm) => !mm.pending));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-150px)] rounded-sm border border-border/60 bg-card overflow-hidden">
      <div className="flex-1 overflow-y-auto px-5 py-6">
        {messages.length === 0 && (
          <div className="max-w-2xl mx-auto pt-10">
            <div
              className="inline-flex items-center justify-center h-12 w-12 rounded-sm mb-4"
              style={{ background: product.gradient, color: "white" }}
            >
              <Sparkles className="h-5 w-5" />
            </div>
            <h2 className="font-serif text-4xl tracking-tight leading-tight">
              How can {product.name.split(" ")[0]} help you today?
            </h2>
            <p className="mt-3 text-muted-foreground">{product.tagline.en}</p>
            {starters.length > 0 && (
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {starters.map((s, i) => (
                  <button
                    key={i}
                    data-testid={`chat-starter-${i}`}
                    onClick={() => send(s)}
                    className="text-start rounded-sm border border-border/60 bg-card hover:border-foreground/30 p-3 text-sm transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {messages.map((m) => (
          <div
            key={m.id}
            className={`max-w-3xl mx-auto py-4 ${m.role === "user" ? "" : ""}`}
            data-testid={`chat-msg-${m.role}`}
          >
            <div className="flex items-start gap-3">
              <div
                className="h-7 w-7 rounded-sm shrink-0 flex items-center justify-center"
                style={
                  m.role === "user"
                    ? { background: "hsl(var(--secondary))", color: "hsl(var(--foreground))" }
                    : { background: product.gradient, color: "white" }
                }
              >
                {m.role === "user" ? <UserIcon className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-2">
                  {m.role === "user" ? "You" : product.name}
                </div>
                {m.pending ? (
                  <div className="flex items-center gap-2 text-muted-foreground text-sm">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking…
                  </div>
                ) : (
                  <div className="prose-sm max-w-none text-foreground">{renderMd(m.content)}</div>
                )}
              </div>
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        className="border-t border-border/60 p-4"
      >
        <div className="max-w-3xl mx-auto flex items-end gap-2">
          <textarea
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={1}
            placeholder={placeholder}
            className="flex-1 resize-none rounded-sm border border-border bg-background p-3 text-sm outline-none focus:border-foreground/40 focus:ring-2 focus:ring-foreground/10 min-h-[44px] max-h-40"
            style={{ "--accent": accent }}
          />
          <button
            type="submit"
            data-testid="chat-send"
            disabled={loading || !input.trim()}
            className="inline-flex h-11 items-center gap-2 rounded-sm px-4 text-sm font-medium text-white disabled:opacity-50"
            style={{ background: product.gradient }}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            <span className="hidden sm:inline">Send</span>
          </button>
        </div>
      </form>
    </div>
  );
}
